/**
 * Live feedback, computed from the walls alone — the solution never reaches the client
 * (CLAUDE.md §10). It flags one thing only: a box pushed into a corner it can never leave.
 *
 * That is not a hint about the answer, it is a fact about the board the player is looking
 * at; and it is sound, like `partial_check` on the server: a box wedged between two
 * perpendicular walls, on a cell that is not a goal, can never move again, so the position
 * is lost. Anything less certain stays silent.
 */
import type { Feedback } from "../types";
import { deadCorners, fromKey, parseBoard, replay } from "./board";
import { cellId, type SokobanDefinition, type SokobanProgress } from "./types";

export function localFeedback(
  definition: SokobanDefinition,
  progress: SokobanProgress,
): Feedback[] {
  const { board, state } = parseBoard(definition.board);
  if (!state) return [];
  const outcome = replay(board, state, progress.moves ?? "");
  const dead = deadCorners(board);
  const stuck = [...outcome.state.boxes].filter((box) => dead.has(box)).sort();
  if (stuck.length === 0) return [];
  return [
    {
      type: "deadlock",
      code: "box_stuck",
      cells: stuck.map((box) => {
        const [r, c] = fromKey(box);
        return cellId(r, c);
      }),
    },
  ];
}
